import Skeleton from '@mui/material/Skeleton';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';

import { CommandBarTypedItem } from '#components/command-bar/components/command-bar-typed-item.js';
import { Toast } from '#components/toast/toast.js';
import { useTRPC } from '#src/trpc.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { resolveAgentConversationOwnerName, resolveAgentConversationRoute } from './agent-conversation-uid-utils.js';

import type { CadstromUidResultProps } from '../cadstrom-uid-types.js';

/**
 * Command-bar result for an agent-workbench conversation id. Shows the conversation
 * title and owner, and on selection navigates to the conversation's standalone
 * page, surfacing a toast when the conversation is unavailable.
 *
 * @param uid - The agent-workbench conversation id to resolve and display.
 * @param onClose - Dismisses the command bar once navigation begins.
 */
export function AgentConversationUidResult({ uid, onClose }: CadstromUidResultProps) {
	const addToast = useAddToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const trpc = useTRPC();
	const queryOptions = trpc.agentChatConversation.get.queryOptions({ conversationId: uid });
	const conversationLookup = useQuery({ ...queryOptions, throwOnError: false });
	const currentUserLookup = useQuery(
		trpc.user.getCurrent.queryOptions(undefined, {
			enabled: conversationLookup.data !== null && conversationLookup.data !== undefined,
			throwOnError: false,
		}),
	);
	const conversation = conversationLookup.data;
	const ownerName =
		conversation ? resolveAgentConversationOwnerName(conversation.ownerId, currentUserLookup.data) : undefined;

	return (
		<Command.Group heading='Agent conversations'>
			<Command.Item
				value={uid}
				keywords={['agent', 'conversation', 'workbench']}
				onSelect={() => {
					void (async () => {
						try {
							const route = await resolveAgentConversationRoute(uid, () =>
								conversationLookup.isSuccess ?
									Promise.resolve(conversationLookup.data)
								:	queryClient.fetchQuery(queryOptions),
							);
							if (!route) {
								showUnavailableToast(addToast);
								return;
							}

							onClose();
							await navigate(route);
						} catch {
							showUnavailableToast(addToast);
						}
					})();
				}}
			>
				{conversationLookup.isPending ?
					<div className='flex min-w-0 flex-1 items-center gap-2 py-0.5'>
						<span className='shrink-0 text-lg font-normal opacity-40'>conversation</span>
						<Skeleton aria-label='Loading conversation' width={180} />
					</div>
				: conversation ?
					<CommandBarTypedItem type='conversation' name={conversation.title ?? 'Untitled conversation'}>
						{currentUserLookup.isPending ?
							<Skeleton aria-label='Loading owner' width={96} />
						:	<span className='truncate font-normal text-gray-a11'>by {ownerName}</span>}
					</CommandBarTypedItem>
				:	<CommandBarTypedItem type='conversation' name='Conversation unavailable'>
						<code className='truncate font-normal text-gray-a11'>{uid}</code>
					</CommandBarTypedItem>
				}
			</Command.Item>
		</Command.Group>
	);
}

function showUnavailableToast(addToast: ReturnType<typeof useAddToast>) {
	addToast(
		<Toast severity='error' title='Conversation unavailable'>
			This conversation was not found or you do not have access.
		</Toast>,
	);
}
